import React, { useEffect, useState } from "react";
import classNames from "classnames/bind";

// We're creating a basic style module that will be used
// You would need to create this file or import existing styles
const styles = {
  button: "button",
};

const cx = classNames.bind(styles);

export interface AlertProps {
  isOpen: boolean;
  header: string;
  message: string;
  buttons: {
    text: string;
    role?: string;
    handler?: () => void;
  }[];
  onClose: () => void;
  cssClass?: string;
  backdropDismiss?: boolean;
  keyboardClose?: boolean;
}

export const CustomAlert = ({
  isOpen,
  header,
  message,
  buttons,
  onClose,
  cssClass,
  backdropDismiss = true,
  keyboardClose = true,
}: AlertProps) => {
  // Handle escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && keyboardClose) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, keyboardClose, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (backdropDismiss) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[99999999]"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50 z-[-1]" onClick={(e) => handleBackdropClick(e)} />
      <div className={`relative bg-white rounded-lg max-w-xs w-full mx-4 overflow-hidden ${cssClass}`}>
        <div className="p-4 text-center">
          {header && <h3 className="text-lg font-semibold mb-2">{header}</h3>}
          {message && <p className="text-sm text-gray-600">{message}</p>}
        </div>
        <div className="flex items-center justify-center border-t border-solid border-t-gray-200">
          {buttons.map((button, index) => (
            <button
              key={index}
              className={cx(
                "w-full p-3 text-center text-sm",
                button.role === "destructive"
                  ? "text-red-500"
                  : button.role === "cancel"
                    ? "text-gray-500"
                    : "text-blue-500",
                index !== 0 ? "border-t" : "border-r-1 border-solid border-r-gray-200",
              )}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (button.handler) button.handler();
                onClose();
              }}
            >
              {button.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export type UseCustomAlertConfig = Omit<AlertProps, "isOpen" | "onClose">;

export const useCustomAlert = () => {
  const [alertConfig, setAlertConfig] = useState<UseCustomAlertConfig>({
    header: "",
    message: "",
    buttons: [],
    cssClass: "",
    backdropDismiss: true,
    keyboardClose: true,
  });
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const customPresentAlert = (config: UseCustomAlertConfig) => {
    setAlertConfig(config);
    setIsAlertOpen(true);
  };

  const closeAlert = () => {
    setIsAlertOpen(false);
  };

  return {
    alertConfig,
    isAlertOpen,
    customPresentAlert,
    closeAlert,
    AlertComponent: () => <CustomAlert isOpen={isAlertOpen} onClose={closeAlert} {...alertConfig} />,
  };
};
